import React, { FC, useCallback } from "react";
import { Map } from "immutable";
import { Hidden, Visible } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { OpenRightPanel } from "types/common/rightPanel";
import { OutgoingRowLayoutTier } from "types/outgoing";
import ServiceLogo from "../../../../components/ServiceLogo/ServiceLogo";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import { cartSelectCart } from "../../../../reduxFolder/selectors/cartSelectors";
import {
  panelSelectData,
  panelSelectOpened,
} from "../../../../reduxFolder/selectors/rightPanelSelectors";
import {
  OutgoingGrowCol,
  OutgoingGrowText,
  OutgoingMainCol,
  OutgoingMetaCol,
} from "../../../../styles/outgoingStyles";
import { OutgoingParcelRow } from "../../../../styles/parcelsStyles";
import formatter from "../../../../utils/formatter";
import {
  SKU_COL_WIDTH,
  STATE_COL_WIDTH,
  WEIGHT_BRUTTO_COL_WIDTH,
} from "../rowLayout";
import { OutgoingCol } from "./NotFinished.styles";

interface NotFinishedProps {
  units: string;
  panelData: any;
  parcel: any;
  cart: any;
  isPanelOpened?: boolean;
  openRightPanel: OpenRightPanel;
  isDemo?: boolean;
  layoutTier?: OutgoingRowLayoutTier;
}

const NotFinished: FC<NotFinishedProps> = React.memo(
  ({
    openRightPanel,
    parcel,
    cart,
    units,
    panelData,
    isPanelOpened,
    isDemo = false,
    layoutTier = "full",
  }) => {
    const { t } = useTranslation("common");
    const deliveryName = parcel.get("delivery");
    const hasDelivery = deliveryName || t("parcels.notSelectedYet");
    const weight = formatter.weight(parcel.get("weight"));
    const isSelectedShipment =
      isPanelOpened &&
      panelData &&
      panelData.get("detailedItemID") === parcel.get("id");
    const showWeightCol = layoutTier !== "narrow";

    // The real panel renders the redux cart; the demo draft has no cart
    // entry, so it opens its own browse-only panel fed by the fixture.
    const clickParcel = useCallback(
      () =>
        isDemo
          ? openRightPanel(
              "DEMO_NOT_FINISHED",
              Map({
                detailedItemID: parcel.get("id"),
                parcel,
                isDemo: true,
              }),
            )
          : openRightPanel(
              "NOT_FINISHED",
              Map({
                detailedItemID: cart.get("id"),
                parcel: cart,
              }),
            ),
      [openRightPanel, parcel, cart, isDemo],
    );

    return (
      <OutgoingParcelRow selected={isSelectedShipment} onClick={clickParcel}>
        <Visible xs>
          <OutgoingCol xs={3}>{parcel.get("sku")}</OutgoingCol>
          <OutgoingCol xs={5}>
            <ServiceLogo serviceKey={deliveryName} />
            {hasDelivery}
          </OutgoingCol>
          <OutgoingCol xs={4}>
            {`${weight} ${t(`units.${units}.primaryShort`)}`}
          </OutgoingCol>
        </Visible>
        <Hidden xs>
          <OutgoingMainCol sm={12}>
            <OutgoingMetaCol $width={SKU_COL_WIDTH} $grow={0}>
              {parcel.get("sku")}
            </OutgoingMetaCol>
            <OutgoingGrowCol>
              <ServiceLogo serviceKey={deliveryName} />
              <OutgoingGrowText>{hasDelivery}</OutgoingGrowText>
            </OutgoingGrowCol>
            {showWeightCol && (
              <OutgoingMetaCol $width={WEIGHT_BRUTTO_COL_WIDTH}>
                {`${weight} ${t(`units.${units}.primaryShort`)}`}
              </OutgoingMetaCol>
            )}
            {/* Same skeleton as the InProgress row (which renders the state
                badge here), so the weight columns stack across sections. */}
            {layoutTier === "full" && (
              <OutgoingMetaCol $width={STATE_COL_WIDTH} />
            )}
          </OutgoingMainCol>
        </Hidden>
      </OutgoingParcelRow>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    isPanelOpened: panelSelectOpened,
    panelData: panelSelectData,
    cart: cartSelectCart,
  }),
  { openRightPanel },
);

export default withConnect(NotFinished);
